import moment from 'lib/momentTimezone';
import { Moment } from 'lib/types';
import { BookingStep } from '@bondvet/types/booking';
import { BOOKING_URL } from 'core/constants';
import { GroupedSlots, PartOfDay, Slot } from './types';
import type { PageMetaDataFragment } from '../../@types/gatsby-types';

export const THRESHOLD_LOW_AVAILABILITY = 4;

export function groupSlots(
    dateStrings: ReadonlyArray<string>,
    timezone: string,
): ReadonlyArray<GroupedSlots> {
    type Aggregator = Partial<Record<PartOfDay, ReadonlyArray<Slot>>>;
    const grouped = dateStrings.reduce<Aggregator>((aggregator, time) => {
        const date = moment(time, moment.ISO_8601).tz(timezone);

        let partOfDay: PartOfDay = 'evening';

        const hour = date.hour();

        if (hour < 12) {
            partOfDay = 'morning';
        } else if (hour < 17) {
            partOfDay = 'afternoon';
        }

        return {
            ...aggregator,
            [partOfDay]: [
                ...(aggregator[partOfDay] || []),
                {
                    label: date.format('LT'),
                    time,
                },
            ],
        };
    }, {});

    return (Object.keys(grouped) as PartOfDay[]).map((partOfDay) => ({
        partOfDay,
        slots: grouped[partOfDay] as ReadonlyArray<Slot>,
    }));
}

export function showAmount(usdCents: number): string {
    return (usdCents / 100).toLocaleString('en-US', {
        maximumFractionDigits: 2,
    });
}

export function parseSlots(
    timezone: string,
    slots: ReadonlyArray<string>,
): ReadonlyArray<Slot> {
    return slots.map((time) => {
        const date = moment(time, moment.ISO_8601).tz(timezone);
        return {
            label: date.format('LT'),
            time,
        };
    });
}

/**
 *
 * event
 * @return text representation of a VCALENDAR iCal file.<br/>
 * e.g:<br/>
 * <pre>
 BEGIN:VCALENDAR
 PRODID:-//Events Calendar//VENDOR 1.0//DE
 VERSION:2.0
 BEGIN:VEVENT
 DTSTAMP:20181124T153543Z
 DTSTART:20181105T000000
 DTEND:20190915T000000
 SUMMARY:Meeting with ...
 DESCRIPTION:Some description, even
 multiline is possible
 URL:https://www.mydomain.de/context/app/do.it?id=435&nr=30
 UID:20181124T153543Z-uidGen@domain.de
 END:VEVENT
 END:VCALENDAR
 * </pre>
 */
export const createIcalEvent = (event: {
    start: Date;
    end: Date;
    summary: string;
    description?: string;
    location?: string;
    url?: string;
}): string => {
    const formatDate = (date: Date): string => {
        if (!date) {
            return '';
        }
        // don't use date.toISOString() here, it will be always one day off (cause of the timezone)
        const day = date.getDate() < 10 ? `0${date.getDate()}` : date.getDate();
        const month =
            date.getMonth() < 9
                ? `0${date.getMonth() + 1}`
                : date.getMonth() + 1;
        const year = date.getFullYear();
        const hour =
            date.getHours() < 10 ? `0${date.getHours()}` : date.getHours();
        const minutes =
            date.getMinutes() < 10
                ? `0${date.getMinutes()}`
                : date.getMinutes();
        const seconds =
            date.getSeconds() < 10
                ? `0${date.getSeconds()}`
                : date.getSeconds();
        return `${year}${month}${day}T${hour}${minutes}${seconds}`;
    };
    let VCALENDAR = `BEGIN:VCALENDAR
PRODID:-//Events Calendar//HSHSOFT 1.0//DE
VERSION:2.0
`;

    const timeStamp = formatDate(new Date());
    const uuid = `${timeStamp}Z-uid@bondvet.com`;
    /**
     * Don't ever format this string template!!!
     */
    const EVENT = `BEGIN:VEVENT
DTSTAMP:${timeStamp}Z
DTSTART:${formatDate(event.start)}
DTEND:${formatDate(event.end)}
SUMMARY:${event.summary}
DESCRIPTION:${event.description}
LOCATION:${event.location}
URL:${event.url}
UID:${uuid}
END:VEVENT`;
    VCALENDAR += `${EVENT}
`;
    VCALENDAR += 'END:VCALENDAR';

    return VCALENDAR;
};

export function createIcalLink(
    date: string,
    // eslint-disable-next-line @typescript-eslint/default-param-last
    durationHours = 1,
    title: string,
    description: string,
    location?: string,
): string {
    const ical = {
        start: moment(date).toDate(),
        end: moment(date).add(durationHours, 'hours').toDate(),
        summary: title,
        description,
        location,
        url: process.env.GATSBY_SITE_URL || 'https://bondvet.com',
    };
    const content = createIcalEvent(ical);

    return `data:text/calendar,${encodeURIComponent(content)}`;
}

export function createAppointmentGoogleCalendarLink(
    isMobile: boolean,
    date: string,
    timezone: string,
    title: string,
    address: string,
    reasonDescription: string,
    notes?: string,
    durationHours = 0.5,
): string {
    const start = moment(date);
    const end = moment(date).add(durationHours * 60, 'minutes');
    const url = isMobile
        ? 'https://calendar.google.com/calendar/gp#~calendar:view=e&bm=1&'
        : 'https://calendar.google.com/calendar/u/0/r/eventedit?';

    const googleCalendarLink = `${url}dates=${start.format(
        'YYYYMMDDTHHmmss',
    )}/${end.format('YYYYMMDDTHHmmss')}&ctz=${encodeURIComponent(
        timezone,
    )}&text=${encodeURIComponent(title)}&location=${encodeURIComponent(
        address,
    )}&details=${encodeURIComponent(`${reasonDescription} - ${notes}`)}`;
    return googleCalendarLink;
}

export function formatWeek(week: Moment): string {
    const start = week.format('MMM YYYY');
    const end = week.clone().add(6, 'days').format('MMM YYYY');

    if (start === end) {
        return start;
    }
    return `${start} - ${end}`;
}

function camelToKebab(camelString: string): string {
    return camelString.replace(/([A-Z])/g, '-$1').toLowerCase();
}

export function getBookingStepUrl(
    step: BookingStep,
    citySlug: string,
    locationSlug: string,
): string {
    switch (step) {
        case 'city':
            return `/${BOOKING_URL}/city`;
        case 'location':
            return `/${BOOKING_URL}/${citySlug}`;
        case 'date':
            return `/${BOOKING_URL}/${citySlug}/${locationSlug}`;
        default:
            return `/${BOOKING_URL}/${citySlug}/${locationSlug}/${camelToKebab(step)}`;
    }
}

export function combinePageMetaDataFragments(
    fragments: readonly (PageMetaDataFragment | undefined)[],
    cityName?: string,
    locationName?: string,
): PageMetaDataFragment {
    return fragments.reduce(
        (accumulator, fragment) => ({
            ...accumulator,
            ...Object.fromEntries(
                Object.entries(fragment ?? {}).map(([key, value]) => [
                    key,
                    (value as string)
                        ?.replace(/{{cityName}}/g, cityName ?? '')
                        ?.replace(/{{locationName}}/g, locationName ?? ''),
                ]),
            ),
        }),
        {},
    );
}
